import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type {
	AiManagedObject,
	AiManagedObjectConfig,
	AiManagedObjectConfigCreateInput,
	AiManagedObjectSourceInput,
} from '@atlassian/ai-managed-objects/types';

import type { AgentManagedExtensionAttributes } from '../../../common/agentManagedExtensionPluginType';
import { findAgentManagedExtensionByLocalId, getNodeAiManagedObject } from '../../../common/utils';
import { resolveCurrentPageUrl } from '../../../services/resolvePageUrl';
import {
	createAiManagedObjectWithCache,
	getAiManagedObjectWithCache,
	reactivateAiManagedObjectWithCache,
} from '../../ai-managed-object-cache';
import { setAgentManagedExtensionMeta } from '../../editor-commands/agent-managed-object-node';
import { getCacheAiManagedObjectMeta } from '../../editor-commands/configure-popup';
import { executeAiManagedObject } from '../../ui/executeAiManagedObject';
import { getAttrsWithAiManagedObject } from './node-attrs';
import {
	getCurrentReferenceObject,
	getCurrentReferenceObjectAri,
	getSourceReferenceObjectAri,
} from './reference';
import type { CopyPasteMarker, CreateCopyPasteIdentityArgs, PendingArchivedRemoval } from './types';

const toConfigCreateInput = (config: AiManagedObjectConfig): AiManagedObjectConfigCreateInput => ({
	desiredUpdateFrequency: config.desiredUpdateFrequency ?? undefined,
	goal: config.goal ?? undefined,
	goalOperations: config.goalOperations ?? undefined,
	prompt: config.prompt ?? null,
	sources:
		config.sources?.map((source) => ({
			app: source.app,
			...(source.url ? { url: source.url } : {}),
		})) ?? undefined,
});

const rebindCurrentPageSources = ({
	config,
	destinationPageUrl,
	sourcePageUrl,
}: {
	config: AiManagedObjectConfigCreateInput;
	destinationPageUrl?: string;
	sourcePageUrl?: string;
}): { config: AiManagedObjectConfigCreateInput; didRebind: boolean } => {
	if (!sourcePageUrl || !destinationPageUrl || sourcePageUrl === destinationPageUrl) {
		return { config, didRebind: false };
	}

	// Current-page sources should follow the destination page when a block is copied.
	let didRebind = false;
	const sources = config.sources?.map((source): AiManagedObjectSourceInput => {
		if (!source.url || source.url !== sourcePageUrl) {
			return source;
		}

		didRebind = true;
		return {
			...source,
			app: 'CONFLUENCE',
			url: destinationPageUrl,
		};
	});

	return {
		config: didRebind ? { ...config, sources } : config,
		didRebind,
	};
};

const setCopiedAiManagedObject = (
	editorView: EditorView,
	localId: string,
	aimo: AiManagedObject,
): void => {
	const found = findAgentManagedExtensionByLocalId(editorView.state, localId);
	if (!found) {
		return;
	}

	const tr = editorView.state.tr;
	tr.setNodeMarkup(found.pos, undefined, {
		...found.node.attrs,
		...getAttrsWithAiManagedObject({
			aimo,
			attrs: found.node.attrs as AgentManagedExtensionAttributes,
		}),
	});
	setAgentManagedExtensionMeta(tr, getCacheAiManagedObjectMeta(aimo, localId));
	editorView.dispatch(tr);
};

const createCopiedAiManagedObject = async ({
	args,
	editorView,
	localId,
	marker,
}: {
	args: CreateCopyPasteIdentityArgs;
	editorView: EditorView;
	localId: string;
	marker: CopyPasteMarker;
}): Promise<{ aimo: AiManagedObject; shouldExecute: boolean } | null> => {
	const referenceObject = getCurrentReferenceObject(args);
	if (!referenceObject) {
		return null;
	}

	const sourceAimo = await getAiManagedObjectWithCache({
		aimoId: marker.sourceAiManagedObjectId,
		editorView,
		localId,
	});
	if (!sourceAimo) {
		return null;
	}

	const { config, didRebind } = rebindCurrentPageSources({
		config: toConfigCreateInput(sourceAimo.config),
		destinationPageUrl: resolveCurrentPageUrl(),
		sourcePageUrl: marker.sourcePageUrl,
	});

	const aimo = await createAiManagedObjectWithCache(
		editorView,
		{
			cloudId: referenceObject.cloudId,
			config,
			referenceObjectAri: referenceObject.referenceObjectAri,
			referenceProduct: 'CONFLUENCE',
			status: 'ACTIVE',
			type: 'BLOCK',
		},
		localId,
	);

	return {
		aimo,
		shouldExecute: marker.operation === 'cut' || didRebind,
	};
};

/** Creates, reactivates, and optionally executes the AIMO represented by a pasted block. */
export const processPastedNode = async ({
	args,
	editorView,
	localId,
	marker,
	pendingArchivedRemovalsByLocalId,
}: {
	args: CreateCopyPasteIdentityArgs;
	editorView: EditorView;
	localId: string;
	marker: CopyPasteMarker;
	pendingArchivedRemovalsByLocalId: Map<string, PendingArchivedRemoval>;
}): Promise<void> => {
	const currentReferenceObjectAri = getCurrentReferenceObjectAri(args);
	const sourceReferenceObjectAri = await getSourceReferenceObjectAri(editorView, marker, localId);
	const isSameReferenceObject =
		Boolean(currentReferenceObjectAri) && currentReferenceObjectAri === sourceReferenceObjectAri;
	const isTrackedSameEditorCut =
		marker.operation === 'cut' && pendingArchivedRemovalsByLocalId.has(localId);

	// Same-reference cuts preserve the existing object id. We wait for any archive request
	// started by the removal transaction so reactivation cannot race the backend state change.
	if (marker.operation === 'cut' && (isTrackedSameEditorCut || isSameReferenceObject)) {
		const pendingRemoval = pendingArchivedRemovalsByLocalId.get(localId);
		await pendingRemoval?.archivePromise;
		const latestPendingRemoval = pendingArchivedRemovalsByLocalId.get(localId);
		if (latestPendingRemoval?.archiveFailed) {
			return;
		}

		await reactivateAiManagedObjectWithCache(editorView, marker.sourceAiManagedObjectId, localId);
		pendingArchivedRemovalsByLocalId.delete(localId);
		return;
	}

	const created = await createCopiedAiManagedObject({ args, editorView, localId, marker });
	if (!created) {
		return;
	}

	if (marker.operation === 'cut') {
		pendingArchivedRemovalsByLocalId.delete(localId);
	}

	setCopiedAiManagedObject(editorView, localId, created.aimo);

	// A cut into a different page, or a copy whose current-page source was rebound,
	// should refresh immediately so the visible block matches its new context.
	if (created.shouldExecute) {
		await executeAiManagedObject({
			aimoId: created.aimo.id,
			editorView,
			localId,
		});
	}
};

/** Handles undo of a cut/delete by reactivating the archived object after the node returns. */
export const processReactivatedRemoval = async ({
	editorView,
	localId,
	pendingArchivedRemovalsByLocalId,
}: {
	editorView: EditorView;
	localId: string;
	pendingArchivedRemovalsByLocalId: Map<string, PendingArchivedRemoval>;
}): Promise<void> => {
	const pendingRemoval = pendingArchivedRemovalsByLocalId.get(localId);
	if (!pendingRemoval) {
		return;
	}

	const found = findAgentManagedExtensionByLocalId(editorView.state, localId);
	const attrs = found?.node.attrs as AgentManagedExtensionAttributes | undefined;
	const currentAimo = getNodeAiManagedObject(attrs);
	if (!found || currentAimo?.id !== pendingRemoval.aiManagedObjectId) {
		return;
	}

	// Undo can restore a cut block after the backend archive was requested; only
	// reactivate after a successful archive so backend lifecycle state stays aligned.
	await pendingRemoval.archivePromise;
	const latestPendingRemoval = pendingArchivedRemovalsByLocalId.get(localId);
	if (latestPendingRemoval?.archiveFailed) {
		return;
	}

	pendingArchivedRemovalsByLocalId.delete(localId);
	await reactivateAiManagedObjectWithCache(editorView, pendingRemoval.aiManagedObjectId, localId);
};
